import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useAuth } from '@/app/contexts/AuthContext';
import {
  hasSeenNotification,
  markNotificationSeen,
  markNotificationsBootstrapped,
  shouldBootstrapNotifications,
} from '@/lib/notifications/dedupStore';
import { playNotificationSound } from '@/lib/notifications/sounds';
import { supabase } from '@/lib/supabase/client';
import { useGuildData } from '@/hooks/useGuildData';
import { formatDateTime } from '@/utils/date';
import { getProfileInGameName } from '@/utils/profile';

export interface MassNotificationItem {
  id: string;
  eventId: string;
  kind: 'published' | 'starting_soon' | 'signup';
  title: string;
  message: string;
  createdAt: string;
}

interface MassNotificationsContextValue {
  notifications: MassNotificationItem[];
  browserPermission: NotificationPermission | 'unsupported';
  dismissNotification: (id: string) => void;
  requestBrowserPermission: () => Promise<void>;
}

const MassNotificationsContext = createContext<MassNotificationsContextValue | null>(null);

function createNotificationId(dedupKey: string): string {
  return dedupKey;
}

function createPublishedNotificationKey(eventId: string): string {
  return `mass-published:${eventId}`;
}

function createStartingSoonNotificationKey(eventId: string, startsAt: string): string {
  return `mass-starting-soon:${eventId}:${startsAt}`;
}

function createSignupNotificationKey(signupId: string, status: string): string {
  return `mass-signup:${signupId}:${status}`;
}

function canUseBrowserNotifications(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

async function showBrowserNotification(title: string, body: string, dedupKey: string): Promise<void> {
  if (!canUseBrowserNotifications()) {
    return;
  }

  if (Notification.permission !== 'granted') {
    return;
  }

  new Notification(title, {
    body,
    tag: dedupKey,
  });
}

export function MassNotificationsProvider({ children }: PropsWithChildren): JSX.Element {
  const { authMode, isApproved, isSignedIn, needsOnboarding, profile } = useAuth();
  const { currentMasses, isLoading, state } = useGuildData();
  const [notifications, setNotifications] = useState<MassNotificationItem[]>([]);
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission | 'unsupported'>(
    canUseBrowserNotifications() ? Notification.permission : 'unsupported',
  );
  const hasInitializedMassSet = useRef(false);
  const knownMassIds = useRef<Set<string>>(new Set());
  const preStartAlertedIds = useRef<Set<string>>(new Set());
  const preStartTimeouts = useRef<Map<string, number>>(new Map());
  const sessionSeenKeys = useRef<Set<string>>(new Set());

  const dismissNotification = useCallback((id: string) => {
    setNotifications((current) => current.filter((notification) => notification.id !== id));
  }, []);

  const pushNotification = useCallback(
    async (
      notification: Omit<MassNotificationItem, 'id' | 'createdAt'> & {
        dedupKey: string;
      },
    ) => {
      if (sessionSeenKeys.current.has(notification.dedupKey)) {
        return;
      }

      if (profile && hasSeenNotification(profile.id, notification.dedupKey)) {
        sessionSeenKeys.current.add(notification.dedupKey);
        return;
      }

      sessionSeenKeys.current.add(notification.dedupKey);

      if (profile) {
        markNotificationSeen(profile.id, notification.dedupKey);
      }

      const nextNotification: MassNotificationItem = {
        ...notification,
        id: createNotificationId(notification.dedupKey),
        createdAt: new Date().toISOString(),
      };

      setNotifications((current) => {
        if (current.some((existing) => existing.id === nextNotification.id)) {
          return current;
        }

        return [nextNotification, ...current].slice(0, 6);
      });
      window.setTimeout(() => dismissNotification(nextNotification.id), 20000);

      const soundKind =
        notification.kind === 'published'
          ? 'mass-published'
          : notification.kind === 'starting_soon'
            ? 'mass-starting-soon'
            : 'mass-signup';

      void playNotificationSound(soundKind);
      void showBrowserNotification(nextNotification.title, nextNotification.message, notification.dedupKey).then(() => {
        if (canUseBrowserNotifications()) {
          setBrowserPermission(Notification.permission);
        }
      });
    },
    [dismissNotification, profile],
  );

  const isStaffListener = Boolean(profile?.roles.some((role) => role === 'owner' || role === 'admin' || role === 'officer' || role === 'mass_manager'));

  const requestBrowserPermission = useCallback(async () => {
    if (!canUseBrowserNotifications()) {
      setBrowserPermission('unsupported');
      return;
    }

    const permission = await Notification.requestPermission();
    setBrowserPermission(permission);
  }, []);

  useEffect(() => {
    if (!canUseBrowserNotifications()) {
      setBrowserPermission('unsupported');
      return;
    }

    setBrowserPermission(Notification.permission);
  }, []);

  useEffect(() => {
    hasInitializedMassSet.current = false;
    knownMassIds.current = new Set();
    preStartAlertedIds.current = new Set();
    sessionSeenKeys.current = new Set();

    for (const timeoutId of preStartTimeouts.current.values()) {
      window.clearTimeout(timeoutId);
    }

    preStartTimeouts.current.clear();
    setNotifications([]);
  }, [profile?.id]);

  useEffect(() => {
    if (isLoading || !isApproved || needsOnboarding) {
      return;
    }

    if (!profile) {
      return;
    }

    const currentIds = new Set(currentMasses.map((event) => event.id));

    if (!hasInitializedMassSet.current) {
      hasInitializedMassSet.current = true;

      if (shouldBootstrapNotifications(profile.id, 'mass-published')) {
        for (const event of currentMasses) {
          markNotificationSeen(profile.id, createPublishedNotificationKey(event.id));
        }

        knownMassIds.current = currentIds;
        markNotificationsBootstrapped(profile.id, 'mass-published');
        return;
      }
    }

    for (const event of currentMasses) {
      const dedupKey = createPublishedNotificationKey(event.id);

      if (!knownMassIds.current.has(event.id) && !hasSeenNotification(profile.id, dedupKey)) {
        void pushNotification({
          dedupKey,
          eventId: event.id,
          kind: 'published',
          title: 'New mass published',
          message: `${event.title} starts at ${formatDateTime(event.startsAt)}.`,
        });
      }
    }

    knownMassIds.current = new Set([...knownMassIds.current, ...currentIds]);
  }, [currentMasses, isApproved, isLoading, needsOnboarding, profile, pushNotification]);

  useEffect(() => {
    for (const timeoutId of preStartTimeouts.current.values()) {
      window.clearTimeout(timeoutId);
    }

    preStartTimeouts.current.clear();

    for (const event of currentMasses) {
      if (!profile) {
        continue;
      }

      const dedupKey = createStartingSoonNotificationKey(event.id, event.startsAt);

      if (preStartAlertedIds.current.has(event.id) || hasSeenNotification(profile.id, dedupKey)) {
        continue;
      }

      const startsAt = new Date(event.startsAt).getTime();
      const alertAt = startsAt - 60_000;
      const now = Date.now();

      if (startsAt <= now) {
        continue;
      }

      if (alertAt <= now) {
        preStartAlertedIds.current.add(event.id);
        void pushNotification({
          dedupKey,
          eventId: event.id,
          kind: 'starting_soon',
          title: 'Mass starting soon',
          message: `${event.title} starts in about 1 minute.`,
        });
        continue;
      }

      const timeoutId = window.setTimeout(() => {
        preStartAlertedIds.current.add(event.id);
        void pushNotification({
          dedupKey,
          eventId: event.id,
          kind: 'starting_soon',
          title: 'Mass starting soon',
          message: `${event.title} starts in about 1 minute.`,
        });
      }, alertAt - now);

      preStartTimeouts.current.set(event.id, timeoutId);
    }

    return () => {
      for (const timeoutId of preStartTimeouts.current.values()) {
        window.clearTimeout(timeoutId);
      }

      preStartTimeouts.current.clear();
    };
  }, [currentMasses, profile, pushNotification]);

  useEffect(() => {
    if (authMode !== 'supabase' || !supabase || !isSignedIn || !isApproved || needsOnboarding || !profile || !isStaffListener) {
      return undefined;
    }

    const client = supabase;
    const channel = client
      .channel(`signup-notifications-${profile.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'signups' }, (payload) => {
        const nextSignup = payload.new as {
          id?: string;
          event_id?: string;
          profile_id?: string;
          status?: string;
        };

        if (!nextSignup.id || !nextSignup.event_id || !nextSignup.profile_id || nextSignup.profile_id === profile.id) {
          return;
        }

        if (nextSignup.status !== 'joined' && nextSignup.status !== 'waitlist') {
          return;
        }

        const event = state.events.find((entry) => entry.id === nextSignup.event_id);
        const sender = state.profiles.find((entry) => entry.id === nextSignup.profile_id);
        const senderName = getProfileInGameName(sender);
        const statusLabel = nextSignup.status === 'waitlist' ? 'joined the waitlist' : 'joined the mass';

        void pushNotification({
          dedupKey: createSignupNotificationKey(nextSignup.id, nextSignup.status),
          eventId: nextSignup.event_id,
          kind: 'signup',
          title: 'New mass signup',
          message: `${senderName} ${statusLabel}${event ? ` for ${event.title}` : ''}.`,
        });
      })
      .subscribe();

    return () => {
      void client.removeChannel(channel);
    };
  }, [authMode, isApproved, isSignedIn, isStaffListener, needsOnboarding, profile, pushNotification, state.events, state.profiles]);

  const value = useMemo<MassNotificationsContextValue>(
    () => ({
      notifications,
      browserPermission,
      dismissNotification,
      requestBrowserPermission,
    }),
    [browserPermission, dismissNotification, notifications, requestBrowserPermission],
  );

  return <MassNotificationsContext.Provider value={value}>{children}</MassNotificationsContext.Provider>;
}

export function useMassNotifications(): MassNotificationsContextValue {
  const context = useContext(MassNotificationsContext);

  if (!context) {
    throw new Error('useMassNotifications must be used within MassNotificationsProvider');
  }

  return context;
}
